import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import config from '../../config';
import { useGlobalContext } from '../../GlobalContext';
import { GetById } from '../../service/BatchService';
import { GetByBatchId } from '../../service/ForumService';

export default function BatchForumList() {
  const {
    isLoading,
    setIsLoading,
    isAppError,
    setAppError,
    appErrorMessage,
    setAppErrorMessage,
    appErrorTitle,
    setAppErrorTitle,
    appErrorMode,
    setAppErrorMode,
    appUser,
  } = useGlobalContext();
  
  const { id } = useParams();

  const [batchDetails, setBatchDetails] = useState({});
  const [forums, setForums] = useState([]);

  const itemsPerPage = 5;
  const [activeTab, setActiveTab] = useState('open');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchBatchDetails = async () => {
    try {
      setIsLoading(true);
      const response = await GetById(id);
      if (response?.batch) {
        setBatchDetails(response.batch);
      } else {
        setBatchDetails({});
      }
    } catch (error) {
      setAppError(true);
      setAppErrorTitle("Error");
      setAppErrorMessage("Failed to fetch batch details");
      setAppErrorMode("error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchForums = async () => {
    try {
      setIsLoading(true);
      const response = await GetByBatchId(id, 1, 1000);
      if (response.status === 200) {
        setForums(response.forums || []);
      }
    } catch (error) {
      setAppError(true);
      setAppErrorTitle("Error");
      setAppErrorMessage("Failed to load data");
      setAppErrorMode("error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBatchDetails();
    fetchForums();
  }, []);

  // Filtered forums based on status
  const filteredForums = forums.filter(
    (forum) => forum.status?.toLowerCase() === activeTab
  );

  // Update pagination info when activeTab or forums change
  useEffect(() => {
    const count = filteredForums.length;
    setTotalItems(count);
    setTotalPages(Math.ceil(count / itemsPerPage));
    setCurrentPage(1); // reset to first page on tab change
  }, [activeTab, forums]);

  // Slice for pagination
  const paginatedForums = filteredForums.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <>
      <Helmet>
        <title>Forum Details | {config.appName}</title>
      </Helmet>

      <div className='container'>
        <div className='page'>
          <div className='page-heading'>
            <h1>Forum Details</h1>
            <span>
              <Link to="/"> Dashboard </Link> / <Link to={`/batch/forum/manage/${id}`}> Add  Forum </Link>
              / Forum Details
            </span>
          </div>

          <div className='page-content'>
            <div className="portal">
              <div className='portal-body'>
                <div className='batch-details'>
                  <div className='row'>
                    <div className='col-lg-3 col-md-3 col-sm-12'>
                      Category : <strong>{batchDetails?.courseId?.categoryId?.name || '-'}</strong>
                    </div>
                    <div className='col-lg-3 col-md-3 col-sm-12'>
                      Sub Category : <strong> {batchDetails?.courseId?.subCategoryIds?.map(sub => sub.name).join(", ")}</strong>
                    </div>
                    <div className='col-lg-6 col-md-6 col-sm-12'>
                      Course : <strong>{batchDetails?.courseId?.name || '-'}</strong>
                    </div>
                  </div>
                </div>
                <br />
                <div className='batch-details'>
                  <div className='row'>
                    <div className='col-lg-3 col-md-3 col-sm-12'>
                      Batch Code : <strong>{batchDetails?.code || '-'}</strong>
                    </div>
                    <div className='col-lg-3 col-md-3 col-sm-12'>
                      Start Date : <strong>
                        {batchDetails?.startDate
                          ? (() => {
                            const date = new Date(batchDetails.startDate);
                            const day = String(date.getDate()).padStart(2, '0');
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const year = date.getFullYear();
                            return `${day}-${month}-${year}`;
                          })()
                          : '-'}
                      </strong>
                    </div>
                    <div className='col-lg-6 col-md-6 col-sm-12'>
                      Batch Name : <strong>{batchDetails?.name || '-'}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-3 text-end">
        <Link to={`/batch/forum/manage/${id}`} className='btn btn-sm btn-success' style={{ marginTop: '5px' }}>
          <FontAwesomeIcon icon="fa-solid fa-plus-circle" /> Add Forum
        </Link>
      </div>
      <div className="container py-2">
        <div className='page-content' style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '20px' }}>
          <div className="forum-tabs d-flex mb-2">
            <button
              className={`tab-btn ${activeTab === 'open' ? 'active' : ''}`}
              onClick={() => setActiveTab('open')}
            >
              Open
            </button>
            <button
              className={`tab-btn ${activeTab === 'closed' ? 'active' : ''}`}
              onClick={() => setActiveTab('closed')}
            >
              Closed
            </button>
          </div>

          <div className="forum-list">
            {paginatedForums.length === 0 ? (
              <div className="text-center py-4">No records found</div>
            ) : (
              paginatedForums.map((forum) => {
                const truncatedTopic =
                  forum.topic.length > 200
                    ? forum.topic.slice(0, 200) + '...'
                    : forum.topic;

                const truncatedDesc =
                  forum.description.length > 250
                    ? forum.description.slice(0, 250) + '...'
                    : forum.description;

                const createdAt = new Date(forum.createdAt);
                const postedDate = `${String(createdAt.getDate()).padStart(2, '0')}-${String(createdAt.getMonth() + 1).padStart(2, '0')}-${createdAt.getFullYear()}`;

                return (
                  <div className="card mb-3 shadow-sm" key={forum._id}>
                    <div className="card-body d-flex align-items-start flex-column flex-md-row">
                      <div className="trainer-avatar me-3 mb-3 mb-md-0">
                        <img
                          src={
                            forum.userId?.image
                              ? `${config.imageBasePath}/users/${forum.userId?._id}.${forum.userId?.image}`
                              : `/${config.imageBasePath}/default/defaultUser.png`
                          }
                          alt={forum.userId?.username}
                          className='img-fluid image-xs avatar-img'
                        />
                      </div>

                      <div className="flex-grow-1 w-100">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start">
                          <div className="d-flex flex-column">
                            <h5 className="mb-1 text-dark">{truncatedTopic}</h5>
                            <p className="text-muted mb-2">{truncatedDesc}</p>
                            <div className="small text-secondary">
                              Posted by <strong className='text-danger'>{forum.userId?.username || 'Unknown'}</strong> on <span className='text-danger'> {postedDate} </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex-grow-1 w-100 mt-2">
                          <div className="d-flex flex-wrap align-items-center mt-3 mt-md-0">
                            <div className="me-3 d-flex align-items-center text-muted">
                              <i className="fa fa-eye me-1"></i> {forum.views || 0}
                            </div>
                            <div className="me-3 d-flex align-items-center text-muted">
                              <i className="fa fa-comments me-1"></i> {forum.counts || 0}
                            </div>
                            <Link
                              to={`/batch/forum-reply/manage/${forum._id}/${id}`}
                              className="btn btn-sm btn-outline-primary"
                            >
                              View
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {totalItems > itemsPerPage && (
            <div className="pagination mt-3 d-flex justify-content-center">
              <span className="align-self-center me-3">
                {currentPage > 1 && (
                  <button
                    className="btn btn-outline-primary me-2"
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Previous
                  </button>
                )}
                Page {currentPage} of {totalPages}
                {currentPage < totalPages && (
                  <button
                    className="btn btn-outline-primary ms-2"
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </button>
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
